import type { BillLine } from './types';
import { lineGross } from './bill';

/**
 * Offers are evaluated on the device (offline). Item offers never stack: each unit gets the
 * single best item offer. Then the best bill-value offer applies to what is left.
 */
export type OfferKind =
  | 'pct'    // value = percent off (bp, 1000 = 10%)
  | 'flat'   // value = paise off per unit (per kg/l for weighed items)
  | 'bxgy'   // buy `buy` get `get` free, same product (cheapest units free)
  | 'multi'  // `buy` units for `value` paise, same product
  | 'bill';  // value = percent (bp) or paise off when subtotal ≥ minBill; `flatBill` picks paise

export interface Offer {
  id: number;
  name: string;
  kind: OfferKind;
  value: number;
  buy?: number;           // bxgy / multi
  get?: number;           // bxgy
  minQty?: number;        // pct / flat: minimum qty of the product (milli-units)
  minBill?: number;       // bill: minimum subtotal (paise)
  maxOff?: number;        // bill pct: cap (paise)
  flatBill?: boolean;     // bill: value is paise rather than bp
  products?: number[];    // empty/undefined = all (pct/flat/bill)
  categories?: string[];
  days?: number[];        // 0 = Sunday … 6; empty = every day
  hours?: [number, number]; // [fromHour, toHour) local time, e.g. [20, 23]
  starts_at?: number | null;
  ends_at?: number | null;
  active: boolean;
}

export interface Applied { offerId: number; name: string; amount: number }
export interface OfferResult {
  lineDisc: number[];   // automatic discount per line (paise)
  lineOffer: Array<number | null>; // offer id that gave each line its discount
  billDisc: number;     // automatic bill-level discount (paise)
  applied: Applied[];   // what the customer saved, per offer
}

export function offerLive(o: Offer, now: Date): boolean {
  if (!o.active) return false;
  const t = now.getTime();
  if (o.starts_at && t < o.starts_at) return false;
  if (o.ends_at && t >= o.ends_at) return false;
  if (o.days?.length && !o.days.includes(now.getDay())) return false;
  if (o.hours) {
    const h = now.getHours() + now.getMinutes() / 60;
    const [from, to] = o.hours;
    if (from <= to ? h < from || h >= to : h < from && h >= to) return false;
  }
  return true;
}

function targets(o: Offer, pid: number, category: string): boolean {
  const hasP = !!o.products?.length, hasC = !!o.categories?.length;
  if (!hasP && !hasC) return true;
  return (hasP && o.products!.includes(pid)) || (hasC && o.categories!.includes(category));
}

/** Discount an item offer gives on all units of one product (qty summed across lines). */
function itemDiscount(o: Offer, qty: number, unitPrice: number, gross: number): number {
  switch (o.kind) {
    case 'pct':
      return qty >= (o.minQty ?? 0) ? Math.round((gross * o.value) / 10000) : 0;
    case 'flat':
      return qty >= (o.minQty ?? 0) ? Math.min(gross, Math.round((o.value * qty) / 1000)) : 0;
    case 'bxgy': {
      const units = Math.floor(qty / 1000);
      const group = (o.buy ?? 1) + (o.get ?? 1);
      return Math.floor(units / group) * (o.get ?? 1) * unitPrice;
    }
    case 'multi': {
      const units = Math.floor(qty / 1000);
      const n = o.buy ?? 2;
      const saving = n * unitPrice - o.value;
      return saving > 0 ? Math.floor(units / n) * saving : 0;
    }
    default:
      return 0;
  }
}

export function applyOffers(lines: BillLine[], categories: string[], offers: Offer[], now = new Date(), manualBillDisc = 0): OfferResult {
  const live = offers.filter((o) => offerLive(o, now));
  const lineDisc = lines.map(() => 0);
  const lineOffer: Array<number | null> = lines.map(() => null);
  const applied = new Map<number, Applied>();
  const credit = (o: Offer, amount: number) => {
    if (amount <= 0) return;
    const a = applied.get(o.id) ?? { offerId: o.id, name: o.name, amount: 0 };
    a.amount += amount;
    applied.set(o.id, a);
  };

  // Group lines by product + price so multi-line weighed items count together.
  const groups = new Map<string, number[]>();
  lines.forEach((l, i) => {
    const k = `${l[0]}:${l[2]}`;
    groups.set(k, [...(groups.get(k) ?? []), i]);
  });

  const itemOffers = live.filter((o) => o.kind !== 'bill');
  for (const idx of groups.values()) {
    const [pid, , price] = lines[idx[0]];
    const qty = idx.reduce((s, i) => s + lines[i][1], 0);
    // Manual line discounts already lower the line; offers work on what is left.
    const room = idx.reduce((s, i) => s + lineGross(lines[i]) - lines[i][3], 0);
    const gross = idx.reduce((s, i) => s + lineGross(lines[i]), 0);
    let best: { o: Offer; amount: number } | null = null;
    for (const o of itemOffers) {
      if (!targets(o, pid, categories[idx[0]] ?? '')) continue;
      const amount = Math.min(room, itemDiscount(o, qty, price, gross));
      if (amount > (best?.amount ?? 0)) best = { o, amount };
    }
    if (!best) continue;
    credit(best.o, best.amount);
    // Spread over the group's lines in order, never below zero per line.
    let left = best.amount;
    for (const i of idx) {
      const cap = lineGross(lines[i]) - lines[i][3];
      const take = Math.min(cap, left);
      lineDisc[i] += take;
      lineOffer[i] = best.o.id;
      left -= take;
    }
  }

  const after = lines.reduce((s, l, i) => s + lineGross(l) - l[3] - lineDisc[i], 0) - manualBillDisc;
  let billDisc = 0;
  let bestBill: Offer | null = null;
  for (const o of live.filter((x) => x.kind === 'bill')) {
    if (after < (o.minBill ?? 0)) continue;
    let amt = o.flatBill ? o.value : Math.round((after * o.value) / 10000);
    if (o.maxOff) amt = Math.min(amt, o.maxOff);
    amt = Math.max(0, Math.min(amt, after));
    if (amt > billDisc) { billDisc = amt; bestBill = o; }
  }
  if (bestBill) credit(bestBill, billDisc);

  return { lineDisc, lineOffer, billDisc, applied: [...applied.values()] };
}

export function describeOffer(o: Offer): string {
  const money = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  switch (o.kind) {
    case 'pct': return `${o.value / 100}% off`;
    case 'flat': return `${money(o.value)} off each`;
    case 'bxgy': return `Buy ${o.buy ?? 1} get ${o.get ?? 1} free`;
    case 'multi': return `${o.buy ?? 2} for ${money(o.value)}`;
    case 'bill': return `${o.flatBill ? money(o.value) : `${o.value / 100}%`} off bills over ${money(o.minBill ?? 0)}${o.maxOff ? ` (max ${money(o.maxOff)})` : ''}`;
  }
}
